<template>
  <div class="document">
    <div class="header">
      <EsHeader>
        <template #header>
          Security Properties
        </template>
        <template #actions v-if="hasModule('SYS:SYS:CONFIGMGR:EDIT')">
          <ul>
            <li class="p-narrow no-border">
              <div class="list-content">
                <a role="button" tabindex="0"
                  @keyup.enter="editPanel()"
                  @click="editPanel()"
                  :class="{ disabled: editOtherPanels }"
                  v-if="!editCurrentPanel"
                  title="Edit"
                >
                  <font-awesome-icon :icon="['far', 'edit']" size="lg"/>
                </a>
              </div>
            </li>
          </ul>
        </template>
      </EsHeader>
    </div>
    <div class="content">
      <es-form style="padding: 1.15rem 0rem; margin: 0">
        <template #es-form>
          <table>
            <tbody>
              <tr>
                <th class="table-header-normal property has-text-grey"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Login Mode: Local Authentication
                </th>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <div
                      class="buttons has-addons nowrap-button"
                      v-if="editCurrentPanel"
                    >
                      <button
                        class="button button--form"
                        v-for="(option, index) in enabledDisabled"
                        :key="index"
                        @click="form.LOCAL_AUTHENTICATION = option.code"
                        :class="{ 'is-active': form.LOCAL_AUTHENTICATION === option.code }"
                      >
                        {{ option.description }}
                      </button>
                    </div>
                    <input
                      v-else
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      type="text"
                      :value="form.LOCAL_AUTHENTICATION === true ? 'Enabled' : 'Disabled'"
                      :aria-label="form.LOCAL_AUTHENTICATION === true ? 'Enabled' : 'Disabled'"
                      :readonly="!editCurrentPanel"
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <th
                  class="table-header-normal property has-text-grey"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Login Mode: SSO / Third-Party
                </th>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <div
                      class="buttons has-addons nowrap-button"
                      v-if="editCurrentPanel"
                    >
                      <button
                        class="button button--form"
                        v-for="(option, index) in enabledDisabled"
                        :key="index"
                        @click="form.SSO_THIRD_PARTY = option.code"
                        :class="{ 'is-active': form.SSO_THIRD_PARTY === option.code }"
                      >
                        {{ option.description }}
                      </button>
                    </div>
                    <input
                      v-else
                      class="input has-text-right is-pulled-right"
                      :class="{ 'is-static': !editCurrentPanel }"
                      type="text"
                      :value="form.SSO_THIRD_PARTY === true ? 'Enabled' : 'Disabled'"
                      :aria-label="form.SSO_THIRD_PARTY === true ? 'Enabled' : 'Disabled'"
                      :readonly="!editCurrentPanel"
                    >
                  </div>
                </td>
              </tr>
              <tr>
                <th
                  class="table-header-normal property has-text-grey"
                  :class="{ 'no-border': editCurrentPanel }"
                >
                  Logout URL
                  <span v-if="editCurrentPanel">
                    (optional)
                  </span>
                </th>
                <td :class="{ 'no-border': editCurrentPanel }">
                  <div class="control">
                    <input
                      class="input has-text-right is-pulled-right"
                      :class="{'is-danger' : errLogoutUrl}"
                      type="text"
                      v-model="form.LOGOUT_URL"
                      :aria-label="form.LOGOUT_URL"
                      v-if="editCurrentPanel"
                      @input="reset('logoutUrl')"
                    >
                    <div class="has-text-right is-pulled-right fix-word-overflow"
                      v-else
                    >
                      {{ form.LOGOUT_URL }}
                    </div>
                    <span v-if="errLogoutUrl" class="help is-danger">
                      {{ msgLogoutUrl }}
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </es-form>
    </div>
    <div
      v-if="editCurrentPanel"
      class="is-flex flex-justify--end flex-align--center"
      style="margin-right: 2rem; padding-bottom: 2rem"
    >
      <button
        class="button button-link has-underline"
        @click.prevent="cancelPanel()"
        style="margin-right: 1rem"
      >
        Cancel
      </button>
      <button
        class="button is-primary"
        @click.prevent="updatePanel()"
      >
        Update
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapActions } from 'vuex';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

export default {
  mixins: [
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsForm,
    EsHeader,
  },
  props: {
    editOtherPanels: {
      type: Boolean,
    },
  },
  data() {
    return this.initialData();
  },
  created() {
    this.enabledDisabled = [
      {
        code: true,
        description: 'Enabled',
      },
      {
        code: false,
        description: 'Disabled',
      },
    ];
  },
  mounted() {
    this.initialize(this.properties);
    this.updateCache();
  },
  computed: {
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
  },
  methods: {
    ...mapActions({
      initProperties: 'propertiesModule/init',
    }),
    cancelPanel() {
      this.undoForm();
      this.hideNotification();
      this.editCurrentPanel = false;
      this.$emit('allowEditOtherPanels');
      this.errLogoutUrl = false;
      this.msgLogoutUrl = null;
    },
    reset(field) {
      if(field === 'logoutUrl') {
        this.errLogoutUrl = false;
        this.msgLogoutUrl = null;
      }
      this.hideNotification();
    },
    checkForFormChanges() {
      return this.compareObjects(this.cache, this.form);
    },
    compareObjects(o, n) {
      return JSON.stringify(o) !== JSON.stringify(n);
    },
    editPanel() {
      this.editCurrentPanel = !this.editCurrentPanel;
      this.$emit('allowEditOtherPanels');
    },
    groupBySection(data, type) {
      return Object.entries(data)
        .filter(([key]) => key.includes(type))
        .map(([key, val]) => ({ [key.replace(type, '')]: val }));
    },
    initialData() {
      return {
        cache: {},
        form: {},
        editCurrentPanel: false,
        errLogoutUrl: false,
        msgLogoutUrl: null,
      };
    },
    initialize(data) {
      this.form = Object.assign({}, ...this.groupBySection(data, 'SECURITY-'));
      this.form.LOCAL_AUTHENTICATION = JSON.parse(this.form.LOCAL_AUTHENTICATION );
      this.form.SSO_THIRD_PARTY = JSON.parse(this.form.SSO_THIRD_PARTY );
    },
    prepForDisplay(type, arrFromDb) {
      const o = {};
      arrFromDb.map((item) => {
        const key = Object.keys(item)[0];
        const value = item[key];
        o[key] = value;
        return 0;
      });
      return o;
    },
    prepForRequest() {
      const payload = [];
      let cached = null;
      let obj = null;
      obj = this.form;
      cached = this.cache;
      this.pushToPayLoad('SECURITY', payload, obj, cached);
      return payload;
    },
    pushToPayLoad(category, payload, obj, cached) {
      Object.entries(obj).forEach(([property, value]) => {
        if (value !== cached[property]) {
          payload.push({ category, property, value });
        }
      });
    },
    undoForm() {
      this.form = JSON.parse(JSON.stringify(this.cache));
    },
    updateCache() {
      this.cache = JSON.parse(JSON.stringify(this.form));
    },
    async updatePanel() {
      this.$emit('allowEditOtherPanels');
      const changes = this.checkForFormChanges();
      if (!changes) {
        this.editCurrentPanel = false;
        this.hideNotification();
        return;
      }
      try {
        await axios({
          method: 'put',
          url: '/properties',
          data: this.prepForRequest(),
        });
        this.updateCache();
        await this.initProperties();
        this.showNotification('success', ['Security Properties have been updated successfully.']);
        this.editCurrentPanel = false;
        this.errLogoutUrl = false;
        this.msgLogoutUrl = null;
      } catch (error) {
        let apiMsg = 'There were some errors.';
        const results = error.response.data;
        if (results) {
          if (results.some(el => el.error === 'Logout url is invalid')) {
            this.errLogoutUrl = true;
            this.msgLogoutUrl = 'Logout URL is invalid.';
            apiMsg = 'There were some errors. Please fix the errors highlighted in the fields below.';
          } else {
            this.errLogoutUrl = false;
            this.msgLogoutUrl = null;
            apiMsg += results.map(r => r.error).join(', ');
          }
        }
        this.showNotification('danger', [apiMsg]);
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "~bulma/sass/utilities/initial-variables.sass"
  @import "~bulma/sass/utilities/mixins.sass"

  @include from($tablet)
    .nowrap-button
      flex-wrap: nowrap
  table th
    vertical-align: middle
  table td
    vertical-align: middle
  a.disabled
    color: #999
    cursor: default
    text-decoration: none
    pointer-events: none
    &:hover
      text-decoration: none
  .property
    padding-left: 0
    font-size: 12px
    width: 48%
  .no-border
    border: 0
  .buttons
    float: right
</style>
